import { useMemo, useState } from 'react';

import {
	InviteAppShortcutsCookieCache,
	useInvitedAppShortcuts as useInvitedAppShortcutsPlatform,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges';

import { Cookies } from '@confluence/cookies';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';

import { useLicensedProducts } from './useLicensedProducts.tsx';

const targetProductsJira = ['jira'] as const;
const targetProductsEmpty = [];

export const useResolveInvitedAppShortcuts = (): UseInvitedAppShortcutsResult => {
	const { cloudId, userId } = useSessionData();
	const { hasJira } = useLicensedProducts();
	const [cacheNeedsRefresh, setCacheNeedsRefresh] = useState(false);

	const userTraits = useExperimentalSiteUserPersonalization({
		skip: !(fg('twcg-460-personalised-invite-notifications-m2-gate') && cacheNeedsRefresh),
		variables: {
			cloudId,
			userId: userId || '',
		},
		fetchPolicy: expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)
			? 'cache-and-network'
			: undefined,
	});

	return useInvitedAppShortcutsPlatform(
		useMemo(
			() =>
				fg('twcg-460-personalised-invite-notifications-m2-gate')
					? {
							enabled: true,
							targetProducts: hasJira ? targetProductsJira : targetProductsEmpty,
							sourceProduct: 'confluence',
							cloudId,
							userId,
							traits: userTraits.data?.experimentalSiteUserPersonalization?.attributes ?? null,
							cache: getInvitedAppShortcutsCache(),
							onCacheNeedsRefresh: () => {
								setCacheNeedsRefresh(true);
							},
						}
					: { enabled: false },
			[userTraits, cloudId, hasJira, userId],
		),
	);
};

let cache: InviteAppShortcutsCookieCache | undefined;

export const getInvitedAppShortcutsCache = (): InviteAppShortcutsCookieCache => {
	if (!cache) {
		cache = new InviteAppShortcutsCookieCache({
			setCookie: Cookies.set,
			getCookie: Cookies.get,
		});
	}
	return cache;
};
